// Tipos e formatadores da área de Cotações. Espelham o que o backend serializa
// em cotacao.service.ts — os campos calculados (resumo/totais/valorEfetivo)
// chegam prontos, a tela não recalcula regra de negócio.

export type StatusCotacao = 'rascunho' | 'em_andamento' | 'finalizada';
export type StatusParte = 'pendente' | 'cotado' | 'sem_custo' | 'nao_aplicavel';
export type ModoValor = 'parte' | 'unico';
export type SecaoParte = 'ponto_risco' | 'pap' | 'pe';

export interface CotacaoParte {
  id: string;
  numero: number;
  secao: SecaoParte;
  titulo: string;
  localizacao: string | null;
  perigos: string[];
  riscos: string[];
  sistemasSeguranca: string[];
  categoria: string | null;
  hrn: number | null;
  classificacao: string | null;
  itensNorma: string | null;
  possiveisSolucoes: string | null;
  adequada: boolean;
  fotoUrl: string | null;
  /** Ampliação 3x gerada na importação, usada no visualizador com zoom. */
  fotoHdUrl: string | null;
  paginaOrigem: number | null;
  valor: number | null;
  valorEfetivo: number;
  status: StatusParte;
  observacoes: string | null;
  ordem: number;
}

export interface CotacaoFoto {
  id: string;
  url: string;
  /** Ampliação 3x gerada na importação, usada no visualizador com zoom. */
  urlHd: string | null;
  legenda: string | null;
  ordem: number;
}

export interface ResumoMaquina {
  totalPartes: number;
  adequadas: number;
  naoAdequadas: number;
  pendentes: number;
  cotadas: number;
  somaPartes: number;
  total: number;
  concluida: boolean;
}

export interface CotacaoMaquina {
  id: string;
  nome: string;
  tag: string | null;
  codigoEquipamento: string | null;
  numeroAr: string | null;
  tipo: string | null;
  setor: string | null;
  fabricante: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  cliente: string | null;
  cidade: string | null;
  arquivoOrigem: string | null;
  /** AR original guardada na importação, para o preview na página do ponto. */
  pdfUrl: string | null;
  modoValor: ModoValor;
  valorUnico: number | null;
  observacoes: string | null;
  ordem: number;
  fotos: CotacaoFoto[];
  partes: CotacaoParte[];
  resumo: ResumoMaquina;
}

export interface TotaisCotacao {
  maquinas: number;
  partes: number;
  adequadas: number;
  naoAdequadas: number;
  pendentes: number;
  valorTotal: number;
}

export interface Cotacao {
  id: string;
  numero: string;
  titulo: string;
  cliente: string | null;
  obra: string | null;
  status: StatusCotacao;
  moeda: string;
  observacoes: string | null;
  validadeDias: number;
  createdAt: string;
  updatedAt: string;
  responsavel: { id: string; nome: string; email?: string } | null;
  squad: { id: string; nome: string } | null;
  maquinas: CotacaoMaquina[];
  totais: TotaisCotacao;
}

export interface CotacaoResumida {
  id: string;
  numero: string;
  titulo: string;
  cliente: string | null;
  obra: string | null;
  status: StatusCotacao;
  createdAt: string;
  responsavel: { id: string; nome: string } | null;
  squad: { id: string; nome: string } | null;
  totais: { maquinas: number; naoAdequadas: number; pendentes: number; valorTotal: number };
}

export interface ResultadoImportacao {
  arquivo: string;
  sucesso: boolean;
  maquinaId?: string;
  maquina?: string;
  partes?: number;
  naoAdequadas?: number;
  avisos?: string[];
  erro?: string;
}

export interface LinhaTabelaFinal {
  maquinaId: string;
  maquina: string;
  tag: string | null;
  setor: string | null;
  numeroAr: string | null;
  parteId: string | null;
  parte: string;
  localizacao: string | null;
  perigos: string[];
  riscos: string[];
  categoria: string | null;
  hrn: number | null;
  classificacao: string | null;
  solucao: string | null;
  observacoes: string | null;
  status: StatusParte;
  valor: number | null;
  fotoParte: string | null;
  fotoParteHd: string | null;
}

export interface TabelaFinal {
  cotacao: Omit<Cotacao, 'maquinas' | 'totais' | 'squad'>;
  linhas: LinhaTabelaFinal[];
  porMaquina: (ResumoMaquina & {
    id: string;
    nome: string;
    tag: string | null;
    setor: string | null;
    numeroAr: string | null;
    modoValor: ModoValor;
    fotos: CotacaoFoto[];
  })[];
  totais: TotaisCotacao;
}

// ---------------------------------------------------------------------------

export const ROTULO_STATUS_COTACAO: Record<StatusCotacao, string> = {
  rascunho: 'Rascunho',
  em_andamento: 'Em andamento',
  finalizada: 'Finalizada',
};

export const CLASSE_STATUS_COTACAO: Record<StatusCotacao, string> = {
  rascunho: 'bg-schmersal-yellow/20 text-schmersal-yellow-dark dark:text-schmersal-yellow border-schmersal-yellow/40',
  em_andamento: 'bg-schmersal-turquoise/15 text-schmersal-blue dark:text-schmersal-turquoise border-schmersal-turquoise/40',
  finalizada: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-300/60',
};

export const ROTULO_STATUS_PARTE: Record<StatusParte, string> = {
  pendente: 'Pendente',
  cotado: 'Cotado',
  sem_custo: 'Sem custo',
  nao_aplicavel: 'Não aplicável',
};

export const ROTULO_SECAO: Record<SecaoParte, string> = {
  ponto_risco: 'Ponto de risco',
  pap: 'Partida / Parada',
  pe: 'Parada de emergência',
};

export function moeda(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Lê o valor digitado pelo vendedor aceitando os dois formatos que aparecem na
 * prática: "12.500,00" (pt-BR, colado da planilha) e "12500.00" (digitado
 * direto). Devolve null quando o campo está vazio.
 */
export function parseValor(texto: string): number | null {
  const limpo = texto.replace(/[^\d.,-]/g, '').trim();
  if (!limpo) return null;

  const temVirgula = limpo.includes(',');
  const normalizado = temVirgula
    ? limpo.replace(/\./g, '').replace(',', '.')
    : limpo;

  const numero = Number(normalizado);
  return Number.isFinite(numero) && numero >= 0 ? numero : null;
}

/** Cor da faixa de risco HRN, seguindo a tabela usada nas apreciações. */
export function corClassificacao(classificacao: string | null): string {
  const c = (classificacao ?? '').toLowerCase();
  if (c.includes('inaceit') || c.includes('extremo')) return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  if (c.includes('muito alto') || c.includes('alto')) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
  if (c.includes('signific')) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  if (c.includes('baixo')) return 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300';
  if (c.includes('aceit')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  return 'bg-schmersal-gray-light text-schmersal-gray-dark dark:bg-schmersal-gray-darker dark:text-schmersal-gray-light';
}
